import React from "react";
import {
  ComposedChart,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { UsersRound } from "lucide-react";

const chartGradients = [
  ["#7C3AED", "#A78BFA"],
  ["#2563EB", "#60A5FA"],
  ["#0891B2", "#22D3EE"],
  ["#059669", "#34D399"],
  ["#D97706", "#FBBF24"],
  ["#DB2777", "#F472B6"],
];

const shortenDepartmentName = (name) => {
  const value = `${name || "Unnamed department"}`.trim();
  return value.length > 22 ? `${value.slice(0, 21)}...` : value;
};

const DepartmentTick = ({ x, y, payload }) => {
  const label = shortenDepartmentName(payload.value);
  const words = label.split(" ");
  const splitAt = words.length > 1 ? Math.ceil(words.length / 2) : words.length;
  const lines = [
    words.slice(0, splitAt).join(" "),
    words.slice(splitAt).join(" "),
  ].filter(Boolean);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={15} textAnchor="middle" fill="#64748B" fontSize={11} fontWeight={600}>
        {lines.map((line, index) => (
          <tspan key={line} x="0" dy={index === 0 ? 0 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const DepartmentTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const department = payload[0]?.payload;

  return (
    <div className="min-w-[170px] rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur-sm">
      <p className="mb-2 max-w-[220px] text-xs font-semibold text-slate-600">
        {department.fullName}
      </p>
      <div className="flex items-center justify-between gap-5">
        <span className="text-xs text-slate-500">Team members</span>
        <span className="text-lg font-bold text-slate-900">
          {department.staff.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const ChartState = ({ children }) => (
  <div className="flex h-[270px] items-center justify-center rounded-2xl bg-slate-50/80 text-sm font-medium text-slate-500">
    {children}
  </div>
);

const UsersByLocationChart = ({ data = [], isLoading = false }) => {
  const chartData = data.map((department, index) => {
    const fullName = `${
      department.departmentName || department.name || "Unnamed department"
    }`.trim();

    return {
      fullName,
      name: fullName,
      staff: Number(department.staffCount || 0),
      gradientId: `department-gradient-${index}`,
      colors: chartGradients[index % chartGradients.length],
    };
  });
  const hasData = chartData.length > 0;
  const totalStaff = chartData.reduce((total, item) => total + item.staff, 0);
  const chartMinWidth = Math.max(560, chartData.length * 92);

  return (
    <section className="relative h-full min-h-[410px] overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-5 font-Nunito shadow-[0_12px_40px_rgba(15,23,42,0.07)] sm:p-6">
      <div className="pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-violet-100/60 blur-3xl" />

      <div className="relative mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
            <UsersRound size={21} strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
              Staff by Department
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Workforce distribution across teams
            </p>
          </div>
        </div>

        {!isLoading && hasData && (
          <div className="rounded-2xl border border-violet-100 bg-violet-50 px-3 py-2 text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-violet-500">
              Total staff
            </p>
            <p className="text-xl font-extrabold leading-tight text-violet-700">
              {totalStaff.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {isLoading ? (
        <ChartState>
          <span className="inline-flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
            Loading department data…
          </span>
        </ChartState>
      ) : !hasData ? (
        <ChartState>No department staff data available.</ChartState>
      ) : (
        <div className="-mx-2 overflow-x-auto px-2 pb-2 [scrollbar-color:#c4b5fd_transparent] [scrollbar-width:thin]">
          <div className="h-[290px]" style={{ minWidth: `${chartMinWidth}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 4, left: -22, bottom: 8 }}
              barCategoryGap="18%"
            >
              <defs>
                {chartData.map((entry) => (
                  <linearGradient
                    key={entry.gradientId}
                    id={entry.gradientId}
                    x1="0"
                    y1="1"
                    x2="0"
                    y2="0"
                  >
                    <stop offset="0%" stopColor={entry.colors[0]} />
                    <stop offset="100%" stopColor={entry.colors[1]} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                stroke="#E2E8F0"
                strokeDasharray="4 6"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                interval={0}
                height={58}
                tick={<DepartmentTick />}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#94A3B8", fontSize: 11, fontWeight: 600 }}
                width={38}
              />
              <Tooltip
                content={<DepartmentTooltip />}
                cursor={{ fill: "#F8FAFC", radius: 12 }}
              />
              <Bar
                dataKey="staff"
                isAnimationActive
                animationDuration={700}
                maxBarSize={30}
                radius={[10, 10, 3, 3]}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.gradientId}
                    fill={`url(#${entry.gradientId})`}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="staff"
                stroke="#6D5DFB"
                strokeWidth={2.5}
                dot={{
                  r: 4,
                  fill: "#FFFFFF",
                  stroke: "#6D5DFB",
                  strokeWidth: 2.5,
                }}
                activeDot={{
                  r: 6,
                  fill: "#6D5DFB",
                  stroke: "#FFFFFF",
                  strokeWidth: 3,
                }}
                connectNulls
                isAnimationActive
                animationDuration={800}
              />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
};

export default UsersByLocationChart;
